'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { Home, DoorOpen, Phone, Search, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { useNavigationStore } from '@/store/use-navigation-store'

const QUICK_LINKS = [
  { label: 'Каталог дверей', icon: DoorOpen, page: 'catalog' as const },
  { label: 'Главная', icon: Home, page: 'home' as const },
  { label: 'Связаться с нами', icon: Phone, page: 'home' as const },
]

const POPULAR_CATEGORIES = [
  'Межкомнатные двери',
  'Входные двери',
  'Раздвижные двери',
  'Двери со стеклом',
]

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

export default function NotFound() {
  const navigate = useNavigationStore()
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate('catalog', { search: searchQuery.trim() })
    }
  }

  return (
    <main className='flex min-h-[70vh] items-center justify-center px-4 py-12'>
      <motion.div
        variants={container}
        initial='hidden'
        animate='show'
        className='w-full max-w-3xl text-center'
      >
        {/* Floating door SVG */}
        <motion.div
          variants={item}
          className='relative mx-auto mb-6 h-32 w-24 md:h-40 md:w-30'
        >
          <svg
            viewBox='0 0 80 120'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='h-full w-full opacity-20'
          >
            <rect
              x='4'
              y='4'
              width='72'
              height='112'
              rx='4'
              stroke='currentColor'
              strokeWidth='3'
              className='text-amber-500'
            />
            <rect
              x='12'
              y='12'
              width='56'
              height='96'
              rx='2'
              fill='currentColor'
              className='text-amber-100 dark:text-amber-900/40'
            />
            <circle
              cx='62'
              cy='65'
              r='4'
              fill='currentColor'
              className='text-amber-500'
            />
          </svg>
          <motion.div
            animate={{ y: [-4, 4, -4] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className='absolute -right-2 -top-2'
          >
            <svg
              viewBox='0 0 80 120'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className='h-20 w-16 opacity-10 md:h-24 md:w-18'
            >
              <rect
                x='4'
                y='4'
                width='72'
                height='112'
                rx='4'
                stroke='currentColor'
                strokeWidth='2'
                className='text-orange-400'
              />
              <circle
                cx='62'
                cy='65'
                r='3'
                fill='currentColor'
                className='text-orange-400'
              />
            </svg>
          </motion.div>
        </motion.div>

        {/* 404 number */}
        <motion.h1
          variants={item}
          className='text-7xl font-black leading-none md:text-9xl'
        >
          <span className='bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 bg-clip-text text-transparent'>
            404
          </span>
        </motion.h1>

        {/* Heading */}
        <motion.h2
          variants={item}
          className='mt-4 text-2xl font-bold md:text-3xl'
        >
          Страница не найдена
        </motion.h2>

        {/* Subtext */}
        <motion.p
          variants={item}
          className='mx-auto mt-3 max-w-md text-muted-foreground'
        >
          К сожалению, такой страницы больше нет. Возможно, она была перемещена
          или вы неправильно ввели адрес.
        </motion.p>

        {/* Search */}
        <motion.div variants={item} className='mx-auto mt-8 flex max-w-md gap-2'>
          <Input
            placeholder='Поиск по каталогу...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className='flex-1'
          />
          <Button onClick={handleSearch} className='min-w-[44px] min-h-[44px]'>
            <Search className='h-4 w-4' />
          </Button>
        </motion.div>

        {/* Quick links */}
        <motion.div variants={item} className='mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3'>
          {QUICK_LINKS.map((link) => (
            <Card
              key={link.label}
              className='cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5'
              onClick={() => navigate(link.page)}
            >
              <CardContent className='flex items-center gap-3 p-4'>
                <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400'>
                  <link.icon className='h-5 w-5' />
                </div>
                <span className='font-medium text-sm'>{link.label}</span>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {/* Popular categories */}
        <motion.div variants={item} className='mt-8'>
          <p className='mb-3 text-sm font-medium text-muted-foreground'>
            Популярные категории
          </p>
          <div className='flex flex-wrap justify-center gap-2'>
            {POPULAR_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                variant='outline'
                size='sm'
                className='rounded-full'
                onClick={() => navigate('catalog', { search: cat })}
              >
                {cat}
                <ArrowRight className='ml-1 h-3 w-3' />
              </Button>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div variants={item} className='mt-10'>
          <Button
            size='lg'
            className='min-h-[44px]'
            onClick={() => navigate('home')}
          >
            <Home className='mr-2 h-4 w-4' />
            Вернуться на главную
          </Button>
        </motion.div>
      </motion.div>
    </main>
  )
}
